import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { getThemeClassName } from 'lib/theme/themeUtils'
import LayoutContainer from '../../../client/components/Common/LayoutContainer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import Image from 'components/Common/Image'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import SiteTakeoverCampaignModal from './SiteTakeoverCampaignModal'

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const Content = styled(Group)`
  transform: translateZ(0);
`

const Banner = styled(Group)`
  padding: ${rem(12)};
  position: relative;
  min-height: ${rem(60)};
`

const SiteTakeoverBackgroundStyle = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
`

// Banner container for offer pages
const BannerContainer = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(12)};
  align-items: center;
  position: relative;

  ${mediaQueryUp.desktop} {
    flex-direction: column;
    padding: 0 ${rem(24)};
    gap: 0;
  }
`

const BannerContent = styled.div`
  display: none;
  position: relative;
  z-index: 1;

  ${mediaQueryUp.desktop} {
    display: flex;
    padding: ${rem(8)} ${rem(150)};
    justify-content: center;
    align-items: center;
    gap: ${rem(20)};
    flex: 1 0 0;
  }
`

const MobileBannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex: 1;
  position: relative;
  z-index: 1;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

// Styled components for offer page
const OfferTextContent = styled.div`
  display: flex;
  flex-direction: column;
`

const OfferTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const OfferSubtitleRow = styled.div`
  display: flex;
  gap: ${rem(8)};
`

const OfferDesktopContent = styled.div`
  gap: ${rem(12)};
  align-items: center;
  display: flex;
`

const OfferDesktopSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const IconWrapper = styled.span`
  display: flex;
  cursor: pointer;
  align-items: center;
`

interface Props {
  vertical?: App.SiteTakeoverVertical
}
function SiteTakeoverOfferPageBanner({ vertical }: Props) {
  const bannerRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(bannerRef, 'static')
  const customTakeover = useCurrentSiteTakeoverV2({ vertical })
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const showModal = useContext(ModalContext)

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const handleModalOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    showModal(<SiteTakeoverCampaignModal />)
  }, [showModal])

  const useLab = !!siteTakeoverTheme && !!siteTakeoverBannerContentPayload
  const useCustom = !useLab && !!customTakeover

  if (!useLab && !useCustom) {
    return null
  }

  if (useCustom && customTakeover?.offerPage) {
    return (
      <CSSBreakpoint min={customTakeover.offerPage.hideOnMobileScreenSize ? 'tablet' : undefined}>
        <Banner ref={bannerRef} direction="horizontal" verticalAlign="center">
          <Background background="default" vertical={vertical} />
          <LayoutContainer>
            <Content
              direction="horizontal"
              gap={12}
              verticalAlign="center"
              horizontalAlign="start"
              tabletHorizontalAlign="center"
              className={getThemeClassName({ mobile: customTakeover.palette })}
            >
              <BodyText variant="medium">
                {customTakeover.offerPage.bannerContent}
              </BodyText>
            </Content>
          </LayoutContainer>
        </Banner>
      </CSSBreakpoint>
    )
  }

  if (useLab) {
    const logoRef = siteTakeoverTheme.logoRef
    const title = siteTakeoverBannerContentPayload.title
    const subtitle = siteTakeoverBannerContentPayload.subtitle
    const ctaLink = siteTakeoverBannerContentPayload.ctaLink
    const backgroundStyle = siteTakeoverTheme.backgroundStyle

    return (
      <BannerContainer>
        {backgroundStyle && <SiteTakeoverBackgroundStyle backgroundStyle={backgroundStyle} />}

        <MobileBannerContent>
          {logoRef && <Image image={{ id: logoRef }} dpr={2} width={32} height={32} />}
          <OfferTextContent>
            <OfferTitleRow>
              <BodyText weight="bold" variant="medium">{title}</BodyText>
              <IconWrapper onClick={handleModalOpen}>
                <LineInfoCircleIcon size="XS" />
              </IconWrapper>
            </OfferTitleRow>
            <OfferSubtitleRow>
              <BodyText variant="small">{subtitle}</BodyText>
              {ctaLink && (
                <TextLink to={ctaLink} weight="bold" size="small">View all</TextLink>
              )}
            </OfferSubtitleRow>
          </OfferTextContent>
        </MobileBannerContent>

        <BannerContent>
          <OfferDesktopContent>
            {logoRef && <Image image={{ id: logoRef }} dpr={2} width={32} height={32} />}
            <BodyText weight="bold" variant="medium">{title}</BodyText>
            <OfferDesktopSubtitle>
              <BodyText variant="medium">{subtitle}</BodyText>
              <IconWrapper onClick={handleModalOpen}>
                <LineInfoCircleIcon size="S" />
              </IconWrapper>
            </OfferDesktopSubtitle>
          </OfferDesktopContent>
        </BannerContent>
      </BannerContainer>
    )
  }
  return null
}

export default SiteTakeoverOfferPageBanner
